import PropTypes from "prop-types";
import { useId, useLayoutEffect } from "react";
import Skeleton from "react-loading-skeleton";

import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "@transfr-inc/dashboard-components/forms";

import { EmptyArray } from "@lib/app.helpers";

import "./goals.container.scss";

export const GoalsContainer = ({
  type,
  goals,
  budgetPlanGoals,
  careerPlanGoals,
  onGoalSelected,
}) => {
  const headerId = useId();

  useLayoutEffect(() => {
    // `DropdownItems` calculates the list size and position incorrectly on
    // first load so that it is placed above the field. It recalculates on
    // resize and scroll, so forcing a resize after the goals are actually
    // rendered seems to fix the issue.
    setTimeout(() => window.dispatchEvent(new Event("resize")), 250);
  }, [goals]);

  const goalsLoader =
    goals?.length === 0
      ? EmptyArray(3).map((_, index) => (
          <Skeleton
            key={`${type}.${index}`}
            height={"2.5rem"}
            containerClassName="tx-skeleton-container"
          />
        ))
      : undefined;

  let description = "";
  let goalIdField = "careerGoalId";
  let placeholder = "Select a career goal...";
  let planGoals = careerPlanGoals;
  let title = "";

  switch (type) {
    case "budget": {
      description = "";
      goalIdField = "budgetGoalId";
      placeholder = "Select a financial goal...";
      planGoals = budgetPlanGoals;
      title = "Financial";

      break;
    }
    case "short": {
      description = "0-6 Months";
      title = "Career Goals (Short Term)";

      break;
    }
    case "long": {
      description = "6+ Months";
      title = "Career Goals (Long Term)";

      break;
    }
  }

  const selectedGoals =
    planGoals?.reduce((acc, planGoal) => {
      const goal = goals.find((goal) => goal.id === planGoal[goalIdField]);
      acc[planGoal.orderNum] = goal;
      return acc;
    }, []) ?? [];
  const availableGoals = (goals ?? []).filter(
    (goal) =>
      !selectedGoals.some((selectedGoal) => selectedGoal.id === goal.id),
  );

  return (
    <div className="goals-container">
      <div className="header" id={headerId}>
        <div className="header-title">{title}</div>
        <div className="header-description">{description}</div>
      </div>
      <div className="content">
        {goalsLoader ??
          EmptyArray(3).map((_, index) => {
            const selectedGoal = selectedGoals[index];

            return (
              <Dropdown
                key={index}
                onOptionSelected={(goal) => onGoalSelected(type, goal, index)}
                selected={selectedGoal}
              >
                <DropdownButton
                  placeholder={placeholder}
                  required={index === 0}
                  clearEnabled
                  aria-labelledby={headerId}
                >
                  {selectedGoal?.text}
                </DropdownButton>
                <DropdownItems>
                  {selectedGoal && (
                    <DropdownItem value={selectedGoal} propName="id">
                      {selectedGoal.text}
                    </DropdownItem>
                  )}
                  {availableGoals.map((goal) => (
                    <DropdownItem key={goal.id} value={goal} propName="id">
                      {goal.text}
                    </DropdownItem>
                  ))}
                </DropdownItems>
              </Dropdown>
            );
          })}
      </div>
    </div>
  );
};

GoalsContainer.propTypes = {
  type: PropTypes.oneOf(["short", "long", "budget"]),
  goals: PropTypes.arrayOf(PropTypes.object),
  budgetPlanGoals: PropTypes.arrayOf(PropTypes.object),
  careerPlanGoals: PropTypes.arrayOf(PropTypes.object),
  onGoalSelected: PropTypes.func,
};
